import _ from 'lodash'
import { toast } from 'react-toastify'
import { Container, Row, Col, Button } from 'reactstrap'
import branditRails from '../api/brandit_rails'
import { STORE_APP_VERSION } from '../constants'
import { addTaxablePriceToCartItems, addPromoData, formatCartItems } from '../helpers/cartItemsFormatter'
import history from '../history'
import { checkStoreVersion, checkApiVersion } from '../hooks/useVersionCheck'
import { STORE_VIEWS_ACTIONS } from '../redux/storeConfig/slices/slice.storeViews/storeViews.types'
import { store } from '../redux/storeConfig/store'
import { formatAddress, legacyLogOut, mergeAddressData } from '../util/utils'
import { logOut, setSessionToken } from './sessions'
import {
  FETCH_BUNDLE,
  FETCH_PRODUCTS,
  CLEAR_ERRORS,
  CREATE_SESSION_ERROR,
  CREATE_SUCCESS_MESSAGE,
  CREATE_CUSTOMER_ADDRESS,
  FETCH_CUSTOMER_ADDRESSES,
  FETCH_PRODUCT_DETAILS_SKU,
  FETCH_MANAGED_CUSTOMER_ADDRESSES,
  CLEAR_MANAGED_CUSTOMER_ADDRESSES_ERROR,
  UPDATE_CUSTOMER_ADDRESS,
  DELETE_CUSTOMER_ADDRESS,
  FETCH_PORTAL_CATEGORY_PRODUCTS,
  FETCH_CLIENT_ORDERS,
  UPDATE_ORDER,
  FETCH_SHIPPING_QUOTE_ERROR,
  CREATE_CODE_ERROR,
  CLEAR_SUCCESS,
  CLEAR_CODE_ERRORS,
  TAX_ERROR,
  CLEAR_TAX_ERROR,
  FETCH_ORDER_DETAILS,
  FETCH_PRODUCT_BUNDLE_SELECTIONS,
  PROOFING_PREVIEW,
  FETCH_PRODUCT_SUGGESTIONS,
  SAVE_SEARCH_QUERY,
  SAVE_PRODUCT_SUGGESTIONS,
  FETCH_ORDER_INVOICE,
  VERIFY_PASSWORD_TOKEN_SUCCESS,
  VERIFY_PASSWORD_TOKEN_FAILURE,
  PRODUCT_ERROR,
  INVENTORY_LOADING,
  FETCH_INVENTORY
} from './types'

const isSessionRequest = url => {
  return (
    url.includes('sessions') ||
    url.includes('/change_password') ||
    url.includes('/reset_password') ||
    url.includes('/portal_core/user_update') ||
    url.includes('/auth0_callback')
  )
}

const isSessionPath = urlPath => {
  const paths = ['log-in', localStorage.getItem('custom_login_url'), 'sso-log-in']
  return paths.some(sessionPath => {
    return urlPath.includes(sessionPath)
  })
}

branditRails.interceptors.response.use(
  response => {
    const apiVersion = response.headers['x-api-version']
    if (response.headers && response.headers.http_guest_user_id) {
      localStorage.setItem('guest_user_id', response.headers.http_guest_user_id)
    }
    // Check API version and store version for new deployments
    checkApiVersion(apiVersion)
    checkStoreVersion()

    return response
  },
  error => {
    // This will capture all errors from API. Use this for global response error handling.
    switch (error?.response?.status) {
      case 401:
        const responseURL = error.response?.request?.responseURL
        // don't redirect on log-in because that API has its own error handling
        const preventRedirect =
          (responseURL && isSessionRequest(responseURL)) || isSessionPath(window.location.pathname)

        // ensure store.dispatch(logOut()) runs whenever /current_user returns unauthorized
        const isCurrentUserRequest = responseURL.includes('/current_user')

        if (preventRedirect && !isCurrentUserRequest) {
          return Promise.reject(error)
          // break
        }
        // regardless of redirect, remove invalid token from storage

        store.dispatch(logOut())
        break
      case 403:
        return Promise.reject(error)
      case 400:
        return Promise.reject(error)
      case 422:
        const errData = error.response?.data
        const isCart = error.config?.url?.endsWith('/cart')
        const isUnsupported = errData?.message?.includes('Unsupported SKUs for Punchout in cart')
        const isERR1 = errData?.control === '_ERR1'
        if (isCart && isUnsupported) {
          return Promise.reject(error)
        } else if (isERR1) {
          // Don't show toaster for _ERR1 errors - they're handled by SweetAlert dialog
          return Promise.reject(error)
        } else {
          const toastMsg = typeof errData === 'string' ? errData : errData?.message || errData?.error || 'Bad request'
          toast.error(toastMsg)
          return Promise.reject(error)
        }
      default:
        return Promise.reject(error)
    }
  }
)

branditRails.interceptors.request.use(config => {
  config.headers['guest_user_id'] = localStorage.getItem('guest_user_id') ? localStorage.getItem('guest_user_id') : ''
  config.headers['X-Location-ID'] = localStorage.getItem('currentlocationId')
    ? localStorage.getItem('currentlocationId')
    : ''
  config.headers['X-Auth-Token'] = localStorage.getItem('authToken') ? localStorage.getItem('authToken') : ''
  // config.headers['X-User-Email']  = localStorage.email
  // config.headers['X-User-ID']     = localStorage.user_id
  // add version to headers
  config.headers['X-Store-App-Version'] = STORE_APP_VERSION
  return config
})

export const fetchPortal = () => async dispatch => {
  const response = await branditRails.get('/site_portal')
  const portalData = response?.data

  if (portalData) {
    setPortalDataInStorage(portalData)
  }
  dispatch({ type: 'FETCH_CUSTOMER_GROUP', payload: portalData?.no_user_customer_group })
  dispatch({ type: 'FETCH_PORTAL', payload: portalData })
  return portalData
}

const setPortalDataInStorage = portalData => {
  localStorage.setItem('coop_def', portalData.context_dictionary?.coop_def)
  localStorage.setItem('ecaid_def', portalData.context_dictionary?.ecaid_def)
  localStorage.setItem('eclid_def', portalData.context_dictionary?.eclid_def)
  localStorage.setItem('ecpid_def', portalData.context_dictionary?.ecpid_def)
  localStorage.setItem('expiry_def', portalData.context_dictionary?.expiry_def)
  localStorage.setItem('location_def', portalData.context_dictionary?.location_def)
  localStorage.setItem('shop_def', portalData.context_dictionary?.shop_def)
  localStorage.setItem('account_credit_def', portalData.context_dictionary?.account_credit_def)
  localStorage.setItem('login_redirect_link_selected', portalData.login_redirect_link_selected)
  localStorage.setItem('login_redirect_link', portalData.login_redirect_link)
  localStorage.setItem('custom_login_url', portalData.custom_login_url)
}

export const fetchPortalPage = page_id => async dispatch => {
  const response = await branditRails.get(`/portal_page/${page_id}`)
  dispatch({ type: 'FETCH_PORTAL_PAGE', payload: response.data })
}

const _enqueuedInvProductIds = []
/**
 * fetchInventory - Fetches inventory for the given product id
 *
 * @param {string} productId - The product id
 * @returns {Function} - A thunk function that dispatches actions
 */
export const fetchInventory = productId => async dispatch => {
  dispatch({ type: INVENTORY_LOADING })
  if (_enqueuedInvProductIds.includes(productId)) {
    return
  }
  _enqueuedInvProductIds.push(productId)
  const response = await branditRails.get(`/inventory/${productId}`)
  dispatch({ type: FETCH_INVENTORY, payload: { data: response.data, productId } })
  _enqueuedInvProductIds.splice(_enqueuedInvProductIds.indexOf(productId), 1)
}

export const fetchProducts =
  (page = null) =>
  async dispatch => {
    let paginationParams = ''
    if (page) {
      paginationParams = `?page=${page}`
      const response = await branditRails.get(`/site_products${paginationParams}`)
      dispatch({ type: 'FETCH_PAGINATED_PRODUCTS', payload: response.data })
    } else {
      const response = await branditRails.get(`/site_products${paginationParams}`)
      dispatch({ type: FETCH_PRODUCTS, payload: response.data })
    }
  }

export const fetchPortalCategoryProducts =
  (categories, locationId, tagFilters, page = null, searchQuery) =>
  async dispatch => {
    let paginationParams = ''
    let searchQueryParams = ''
    let categoryParams = ''
    let tagFiltersParams = ''

    if (searchQuery) {
      searchQueryParams = `&search=${searchQuery}`
    }
    if (tagFilters) {
      tagFiltersParams = `&tag_filters=${JSON.stringify(tagFilters)}`
    }
    if (page) {
      paginationParams = `&page=${page}`
      if (categories && categories.length !== 0) {
        categoryParams = `&categories=${categories}`
      }
      const response = await branditRails.post(
        `/site_products_for_categories?location_id=${locationId}${categoryParams}${paginationParams}${searchQueryParams}${tagFiltersParams}`
      )
      if (response && response.data) {
        dispatch({ type: 'FETCH_PAGINATED_PRODUCTS', payload: response.data })
      }
    } else {
      const response = await branditRails.post(
        `/site_products_for_categories?&location_id=${locationId}${searchQueryParams}${tagFiltersParams}`
      )
      if (response && response.data) {
        dispatch({ type: FETCH_PORTAL_CATEGORY_PRODUCTS, payload: response.data })
      }
    }
  }

export const fetchLocationProducts =
  (tagFilters, page = null, searchQuery) =>
  async dispatch => {
    let paginationParams = ''
    let searchQueryParams = ''
    let tagFilterParams = ''

    if (searchQuery) {
      searchQuery = searchQuery.replace('&', '')
      searchQueryParams = `&search=${searchQuery}`
    }

    if (tagFilters) {
      tagFilterParams = `&tag_filters=${JSON.stringify(tagFilters)}`
    }

    if (page) {
      paginationParams = `&page=${page}`
      const response = await branditRails.post(
        `/site_products?${paginationParams}${searchQueryParams}${tagFilterParams}`
      )
      dispatch({ type: 'FETCH_PAGINATED_PRODUCTS', payload: response.data })
    } else {
      const response = await branditRails.post(
        `/site_products?${paginationParams}${searchQueryParams}${tagFilterParams}`
      )
      dispatch({ type: FETCH_PRODUCTS, payload: response.data })
    }
  }

export const saveSearchQuery = data => ({
  type: SAVE_SEARCH_QUERY,
  payload: data
})

export const saveProductSuggestions = data => ({
  type: SAVE_PRODUCT_SUGGESTIONS,
  payload: data
})

export const fetchLocations =
  (search = '', page = 0) =>
  async dispatch => {
    const response = await branditRails.get(`/site_locations?search=${search}&page=${page}`)
    if (response && response.data) {
      return response.data
    }
  }

export const fetchSelectedBundle = (bundle_id, formValues) => async dispatch => {
  const response = await branditRails.post(`/bundle/${bundle_id}`, formValues)
  dispatch({ type: FETCH_BUNDLE, payload: response.data })
}

export const fetchLocationProduct = product_id => async dispatch => {
  dispatch({ type: 'CLEAR_PRODUCT' })
  await branditRails.get(`/site_product/${product_id}`).then(response => {
    dispatch({ type: 'FETCH_PRODUCT_DETAILS', payload: response.data })
  })
}

export const clearProductPage = () => async dispatch => {
  dispatch({ type: 'CLEAR_PRODUCT' })
}

export const fetchPortalProductSku = (product_id, portal_id, sku) => async dispatch => {
  const response = await branditRails.get(`/site_product/${product_id}?portal_id=${portal_id}&product_sku=${sku}`)
  dispatch({ type: FETCH_PRODUCT_DETAILS_SKU, payload: response.data })
}

// export const fetchLocationProductSku = (product_id, location_id, sku) => async dispatch => {
//   const response = await branditRails.get(`/site_product/${product_id}?location_id=${location_id}&product_sku=${sku}`);
//   dispatch({ type: FETCH_PRODUCT_DETAILS_SKU,  payload: response.data})
// };

export const fetchProductNameSuggestions = searchQuery => async dispatch => {
  if (searchQuery === '') {
    dispatch({ type: FETCH_PRODUCT_SUGGESTIONS, payload: [] })
    return
  }
  const response = await branditRails.get(`site_product/get_suggestions/${searchQuery}`).then(function (response) {
    dispatch({ type: FETCH_PRODUCT_SUGGESTIONS, payload: response.data })
  })
}

export const fetchUserCredit = () => async dispatch => {
  try {
    const response = await branditRails.get(`/user_credit`)
    dispatch({ type: 'FETCH_USER_CREDIT', payload: response.data })

    return response
  } catch (error) {
    console.error('Failed to fetch user credit:', error)
    return null
  }
}

export const clearErrors = () => async dispatch => {
  dispatch({ type: CLEAR_ERRORS, payload: '' })
}

export const createUser = formValues => async dispatch => {
  dispatch({ type: CLEAR_ERRORS, payload: '' })
  return await branditRails
    .post('/users', formValues)
    .then(function (response) {
      dispatch({ type: CREATE_SUCCESS_MESSAGE, payload: 'Account Created.' })
      return response
    })
    .catch(function (error) {
      if (error.response.status === 422) {
        dispatch({ type: 'CREATE_USER_ERROR', payload: [error.response.data] })
      } else if (
        error.response.status === 500 &&
        error.response.data &&
        error.response.data.exception &&
        error.response.data.exception.includes('index_users_on_username_and_client_id')
      ) {
        dispatch({
          type: 'CREATE_USER_ERROR',
          payload: { message: 'Username Already In Use. Please Use a Different One' }
        })
      } else {
        console.error(error.response)
      }
      return error
    })
}

export const fetchUser = () => async dispatch => {
  return await branditRails
    .get(`/current_user`)
    .then(response => {
      if (response?.data?.id) {
        dispatch({ type: 'SET_CURRENT_USER', payload: response.data })
        dispatch({ type: STORE_VIEWS_ACTIONS.SET_STORE_VIEWS_AVAILABLE, payload: response.data.all_user_locations })
        dispatch({ type: 'FETCH_CUSTOMER_GROUP', payload: response.data.customer_group })
      }
      return response
    })
    .catch(error => {
      return error
    })
}

export const verifyPasswordToken = token => {
  return dispatch => {
    branditRails
      .post('/verify_password_token', { authtoken: token })
      .then(response => {
        dispatch({ type: VERIFY_PASSWORD_TOKEN_SUCCESS, payload: response.data })
      })
      .catch(error => {
        if (error.response && error.response.data) {
          dispatch({ type: VERIFY_PASSWORD_TOKEN_FAILURE, payload: error.response.data })
        } else {
          dispatch({
            type: VERIFY_PASSWORD_TOKEN_FAILURE,
            payload: { error: 'Network error or response data is undefined' }
          })
        }
      })
  }
}

export const resetUserPassword = formValues => async dispatch => {
  dispatch({ type: CLEAR_ERRORS, payload: '' })
  await branditRails
    .put('/user_reset_password', formValues)
    .then(function (response) {
      dispatch({ type: CREATE_SUCCESS_MESSAGE, payload: 'Email sent. You will receive a link to reset your password.' })
    })
    .catch(function (error) {
      toast.error(error.response.data.error, {
        position: toast.POSITION.TOP_CENTER,
        type: toast.TYPE.DANGER,
        className: 'error-toast',
        autoClose: 3000
      })
    })
}

export const clearSuccessMessage = () => async dispatch => {
  dispatch({ type: 'CLEAR_SUCCESS', payload: [] })
}

export const resetPassword = formValues => async dispatch => {
  dispatch({ type: CLEAR_ERRORS, payload: '' })
  await branditRails
    .post('/reset_password', formValues)
    .then(function (response) {
      dispatch({ type: CREATE_SUCCESS_MESSAGE, payload: 'New Password sent. You can login again.' })
    })
    .catch(function (error) {})
}

export const changePassword = formValues => async dispatch => {
  return await branditRails
    .post('/change_password', formValues)
    .then(response => {
      dispatch({ type: CREATE_SUCCESS_MESSAGE, payload: 'New password set successfully.' })
      return response
    })
    .catch(error => {
      if (error.response.data.error_code && error.response.data.error_code === 'cannot_reuse') {
        toast.error('You may not reuse your old password.', {
          position: toast.POSITION.TOP_CENTER,
          type: toast.TYPE.DANGER,
          className: 'error-toast',
          autoClose: 3000
        })
        return 'invalid'
      } else if (error.response.data.error_code && error.response.data.error_code === 'incorrect_password') {
        toast.error('Incorrect password. Please try again.', {
          position: toast.POSITION.TOP_CENTER,
          type: toast.TYPE.DANGER,
          className: 'error-toast',
          autoClose: 3000
        })
      } else if (error.response.data.error_code && error.response.data.error_code === 'not_found') {
        toast.error('No user found with that e-mail address. Please create an account.', {
          position: toast.POSITION.TOP_CENTER,
          type: toast.TYPE.DANGER,
          className: 'error-toast',
          autoClose: 3000
        })
      } else if (error.response.data.error_code && error.response.data.error_code === 'not_expired') {
        toast.error('Your password is not yet expired. Please log in normally.', {
          position: toast.POSITION.TOP_CENTER,
          type: toast.TYPE.DANGER,
          className: 'error-toast',
          autoClose: 3000
        })
      }
      return error.response
    })
}

export const resendEmailConfirmation = formValues => async dispatch => {
  dispatch({ type: CLEAR_ERRORS, payload: '' })
  await branditRails
    .post('/users/confirmation', formValues)
    .then(function (response) {
      dispatch({ type: CREATE_SUCCESS_MESSAGE, payload: 'New confirmation link sent.' })
      dispatch(
        dispatchSweetAlert({
          type: 'success',
          title: 'Confirmation Email Resent',
          alertMessage: 'If an account exists with the email provided, you will receive the confirmation link shortly.',
          onConfirm: () => history.push('/log-in'),
          confirmBtnCssClass: 'mf-primary-btn alert-width-btn',
          confirmBtnText: 'Back to Sign In',
          showCancel: false
        })
      )
    })
    .catch(function (error) {})
}

export const updateUser = formValues => async dispatch => {
  dispatch({ type: CLEAR_ERRORS, payload: '' })
  await branditRails
    .patch('/user_update', formValues)
    .then(function (response) {
      dispatch({ type: CREATE_SUCCESS_MESSAGE, payload: 'Account Updated.' })
    })
    .catch(function (error) {
      let errorMessage = ''
      switch (error.response.status) {
        case 401:
          errorMessage = { message: 'Password invalid. ' }
          dispatch({ type: CREATE_SESSION_ERROR, payload: errorMessage })
          break
        default:
          return null
      }
      dispatch({ type: 'SET_SESSION_STATUS', payload: 'FAILED' })
    })
}

export const createSession = values => async dispatch => {
  dispatch({ type: CLEAR_ERRORS, payload: '' })
  dispatch({ type: 'SET_SESSION_STATUS', payload: 'LOADING' })
  return await branditRails
    .post('/user_sessions', values)
    .then(response => {
      if (response?.data?.auth_token) {
        dispatch(setSessionToken(response.data.auth_token))
        dispatch({ type: 'SET_SESSION_STATUS', payload: 'SUCCESS' })
        dispatch({ type: 'CLEAR_STATUS' })
      } else {
        legacyLogOut()
        dispatch(deleteSession())
      }
      return response
    })
    .catch(error => {
      let errorMessage = ''
      let responseMsg = ''

      switch (error?.response?.status) {
        case 401:
          if (error.response?.data?.error_code === 'not_active') {
            errorMessage = { message: 'Your account is not active' }
            toast.error(errorMessage.message, {
              position: toast.POSITION.TOP_CENTER,
              type: toast.TYPE.DANGER,
              className: 'error-toast',
              autoClose: 3000
            })
          } else if (error.response.data.error_code && error.response.data.error_code === 'auth0_login_failed') {
            errorMessage = { message: 'Auth0 login failed. Please try again.' }
            toast.error(errorMessage.message, {
              position: toast.POSITION.TOP_CENTER,
              type: toast.TYPE.DANGER,
              className: 'error-toast',
              autoClose: 3000
            })
          } else if (error.response.data.error_code && error.response.data.error_code === 'account_not_found') {
            errorMessage = { message: 'No account found.' }
            toast.error("We couldn't find an account associated with your email address.", {
              position: toast.POSITION.TOP_CENTER,
              type: toast.TYPE.DANGER,
              className: 'error-toast',
              autoClose: 4000
            })
          } else if (error.response.data.error_code && error.response.data.error_code === 'domain_not_whitelisted') {
            errorMessage = { message: 'Email domain not white listed.' }
            toast.error('The domain of the provided email is not allowed for registration.', {
              position: toast.POSITION.TOP_CENTER,
              type: toast.TYPE.DANGER,
              className: 'error-toast',
              autoClose: 4000
            })
          } else if (error.response.data.error_code && error.response.data.error_code === 'signup_disabled') {
            errorMessage = {
              message:
                'We couldn’t find an account, for this store, with that email address. Please double-check your entry or try a different email.'
            }
            toast.error(errorMessage.message, {
              position: toast.POSITION.TOP_CENTER,
              type: toast.TYPE.DANGER,
              className: 'error-toast',
              autoClose: 3000
            })
          } else if (error.response?.data?.error_code === 'password-expired') {
            errorMessage = { message: 'Your password has expired ' }
            toast.error('Password needs to be reset', {
              position: toast.POSITION.TOP_CENTER,
              type: toast.TYPE.DANGER,
              className: 'error-toast',
              autoClose: 3000
            })
            responseMsg = 'password-expired'
          } else if (error.response?.data?.error_code === 'not_confirmed') {
            errorMessage = { message: 'Please click the confirmation link in your email.' }
            responseMsg = {
              error_type: 'not_confirmed',
              message: 'Please click the confirmation link in your email.'
            }
          } else if (error.response?.data?.error_code === 'not_found') {
            errorMessage = { message: 'No user found with that e-mail address.' }
            toast.error('No user found with that e-mail address. Please create an account.', {
              position: toast.POSITION.TOP_CENTER,
              type: toast.TYPE.DANGER,
              className: 'error-toast',
              autoClose: 4000
            })
            responseMsg = 'redirect'
          } else if (error.response?.data?.error_code === 'portal-not-authorized') {
            errorMessage = {
              message:
                'We couldn’t find an account, for this store, with that email address. Please double-check your entry or try a different email.'
            }
            toast.error(errorMessage.message, {
              position: toast.POSITION.TOP_CENTER,
              type: toast.TYPE.DANGER,
              className: 'error-toast',
              autoClose: 3000
            })
            responseMsg = 'invalid'
          } else {
            errorMessage = { message: 'Email or password invalid.' }
            toast.error(errorMessage.message, {
              position: toast.POSITION.TOP_CENTER,
              type: toast.TYPE.DANGER,
              className: 'error-toast',
              autoClose: 3000
            })
            responseMsg = 'invalid'
          }
          if (errorMessage) {
            dispatch({ type: CREATE_SESSION_ERROR, payload: errorMessage })
          } else {
            dispatch({
              type: CREATE_SESSION_ERROR,
              payload: {
                message: 'An error occurred. Please try again. If the problem persists, contact support for assistance.'
              }
            })
          }
          break
        case 403:
          errorMessage = { message: 'Your account is not yet approved' }
          toast.error('Your account is not yet approved. ', {
            position: toast.POSITION.TOP_CENTER,
            type: toast.TYPE.DANGER,
            className: 'error-toast',
            autoClose: 3000
          })

          dispatch({ type: CREATE_SESSION_ERROR, payload: errorMessage })
          return 'not-yet-approved'
        case 422:
          toast.error('Invalid username or password')
          break
        case 500:
        default:
          responseMsg = {
            // data: {},
            // status: 500,
            message: 'An error occurred. Please try again. If the problem persists, contact support for assistance.'
          }
          break
      }
      dispatch({ type: 'SET_SESSION_STATUS', payload: 'FAILED' })
      dispatch(setLoading('session', false))
      return responseMsg
    })
}

export const deleteSession = () => async dispatch => {
  await branditRails.delete('/user_sessions').then(function (response) {
    localStorage.setItem('user_token', '')
    localStorage.setItem('email', '')

    localStorage.setItem('currentlocationId', '')

    dispatch({ type: 'CLEAR_CURRENT_USER' })
    dispatch({ type: 'CLEAR_CART' })
    dispatch({ type: 'CLEAR_USER_CREDIT' })
  })
}

export const fetchMaxQuantities =
  ({ productIds, customerGroupId }) =>
  async dispatch => {
    const formData = new FormData()

    formData.append('ids', JSON.stringify(productIds))
    formData.append('customer_group_id', customerGroupId)
    const response = await branditRails.post('/product_quantity_available', formData)
    if (response && !_.isEmpty(response)) {
      dispatch({ type: 'SET_MAX_QUANTITIES', payload: response.data })
    }
  }

export const fetchConfigurableSelection = (id, locationId, selectedOptions) => async dispatch => {
  const response = await branditRails.get(
    `/bundle_products/${id}/selection?location_id=${locationId}&selected_options=${selectedOptions}`
  )
  dispatch({ type: FETCH_PRODUCT_BUNDLE_SELECTIONS, payload: response.data })
}

//
// STOCK CHECK FUNCTIONS
export const fetchStockCount =
  (sku, portal_id, location_id, configOptionIds, productId, bundleGroupProductId = null, productSkuId = null) =>
  async dispatch => {
    dispatch({ type: 'PRODUCT_LOADING' })
    // Check if configOptionIds/productSkuId is null or undefined and use an empty string as a fallback
    const configOptionIdsParam = configOptionIds ? `configurable_option_ids=${configOptionIds}` : ''
    const productSkuIdParam = productSkuId ? `product_sku_id=${productSkuId}` : ''

    // create query params string
    const queryParams = [
      `portal_id=${portal_id}`,
      `location_id=${location_id}`,
      configOptionIdsParam,
      `sku=${sku}`,
      productSkuIdParam
    ]
      .filter(param => param) // remove empty params
      .join('&')

    const response = await branditRails.get(`/stock_check/${productId}?${queryParams}`)

    if (response.data.product_sku_id === 'not-found') {
      dispatch({ type: 'CLEAR_PRODUCT_LOADING' })
      return response
    }
    dispatch({ type: 'FETCH_STOCK_COUNT', payload: response.data.inventory, sku })
    dispatch({
      type: 'ADD_PRODUCT_SKU_ID_TO_PRODUCT',
      payload: response.data.product_sku_id,
      bundleGroupProductId
    })
    dispatch({ type: 'CLEAR_PRODUCT_LOADING' })

    return response
  }
// END STOCK CHECK

// CART FUNCTIONS
export const addItemToCart = formValues => async dispatch => {
  dispatch({ type: 'PRODUCT_LOADING' })
  await branditRails
    .post('/cart', formValues)
    .catch(err => {
      console.error(err)
      throw err
    })
    .finally(() => {
      dispatch({ type: 'CLEAR_PRODUCT_LOADING' })
      dispatch({ type: 'CLEAR_PROOFING_PREVIEW' })
    })
}

export const clearCartItems = () => async dispatch => {
  dispatch({ type: 'CLEAR_CART' })
}

export const fetchCartItems = (userId, portal, location) => async (dispatch, getState) => {
  dispatch({ type: 'SET_CART_LOADING' })
  const customerGroup = getState().customerGroup
  try {
    const resp = await branditRails.get('/cart')
    const cartItems = formatCartItems(resp.data.cart_items, portal, location)

    dispatch({ type: 'SET_CART_ITEMS', payload: cartItems })
    dispatch({ type: 'SET_USER_CART_ID', payload: resp.data.user_cart_id })

    // Only fetch max quantities if we have cart items and customer group
    if (cartItems.length > 0 && customerGroup && customerGroup.id > 0) {
      const formData = new FormData()
      const productIdsArray = cartItems.map(ci => ci.product_id).filter(n => n)

      // Skip if no valid product IDs
      if (productIdsArray.length > 0) {
        formData.append('ids', JSON.stringify(productIdsArray))
        formData.append('customer_group_id', customerGroup.id)

        // fetch max qty for cart items if user is logged in
        branditRails
          .post('/product_quantity_available', formData)
          .then(response => {
            if (response && !_.isEmpty(response)) {
              dispatch({ type: 'SET_MAX_QUANTITIES', payload: response.data })
            }
          })
          .finally(() => {
            dispatch({ type: 'MAX_QUANTITY_LOADING', payload: false })
          })
      } else {
        dispatch({ type: 'MAX_QUANTITY_LOADING', payload: false })
      }
    } else {
      dispatch({ type: 'MAX_QUANTITY_LOADING', payload: false })
    }

    return cartItems
  } catch (error) {
    if (
      error.response?.status === 422 &&
      error.response.data.message.includes('Unsupported SKUs for Punchout in cart')
    ) {
      const message = error.response.data.message

      const handleRemove = async () => {
        try {
          const resp = await branditRails.post('/cart/delete_unsupported_punchout_cart_items')
          toast.success(resp.data.message)
          store.dispatch(fetchCartItems())
        } catch (e) {
          toast.error('Failed to remove unsupported items.')
        } finally {
          store.dispatch(clearSweetAlert())
        }
      }

      const handleLogout = () => {
        store.dispatch(logOut())
        store.dispatch(clearSweetAlert())
        window.location.reload()
      }

      store.dispatch(
        dispatchSweetAlert({
          type: 'warning',
          title: 'Unsupported Items in Cart',
          alertMessage: message,
          confirmBtnText: 'Remove Unsupported Items',
          onConfirm: handleRemove,
          cancelBtnText: 'Logout',
          onCancel: handleLogout,
          showCancel: true,
          showConfirm: true,
          showCloseButton: false,
          closeOnClickOutside: false
        })
      )

      return null
    } else {
      console.error(error)
      toast.error('Failed to fetch cart items.')
      return null
    }
  } finally {
    dispatch({ type: 'CLEAR_CART_LOADING' })
  }
}

export const updateCartItems = newCartItems => async dispatch => {
  dispatch({ type: 'UPDATE_CART_ITEMS', payload: newCartItems })
}

export const deleteCartItem = cartItemId => async dispatch => {
  await branditRails.post(`/cart/delete/${cartItemId}`).then(resp => {
    if (resp && resp.status === 200) {
      // Clear multi-address selection for this item BEFORE updating cart items
      // This ensures the shipping group configuration won't include stale references
      // to the deleted cart item when the Cart.js useEffect fires
      dispatch({ type: 'CLEAR_MULTI_ADDRESS_SELECTION', payload: cartItemId })
      dispatch({ type: 'DELETE_CART_ITEM', payload: resp.data })
    } else {
      toast.error('Could not delete item from cart')
    }
  })
}

export const updateCartItem = (cartItemId, qty) => async dispatch => {
  dispatch({ type: 'CART_ITEM_LOADING', payload: cartItemId })
  await branditRails
    .patch(`/cart/update/${cartItemId}/${qty}`)
    .then(resp => {
      if (resp && resp.status === 200) {
        dispatch({ type: 'UPDATE_CART_ITEM', payload: resp.data })
      } else {
        toast.error('Could not update item')
      }
      dispatch({ type: 'CLEAR_CART_ITEM_LOADING', payload: cartItemId })
    })
    .catch(err => {
      toast.error('Error: Could not update item')
      dispatch({ type: 'CLEAR_CART_ITEM_LOADING', payload: cartItemId })
    })
}

export const checkStockCartItem = (cartItemId, newQuantity) => async dispatch => {
  const options = {
    cart_item_id: cartItemId,
    quantity: newQuantity,
    sku: null
  }

  await branditRails.post('/inventory/stock_check_one', options)
}

export const clearCart = locationId => async dispatch => {
  await branditRails
    .post('/cart/clear_all', { location_id: locationId })
    .then(response => {
      if (response && response.status === 200) {
        dispatch({ type: 'CLEAR_CART' })
        toast.success('Your cart has been cleared')
        dispatch({ type: 'CLEAR_SWEETALERT' })
      }
    })
    .catch(err => {
      toast.error(
        "Sorry! We've experienced an error with removing 1 or more products from your cart. Please contact our support team for assistance."
      )
    })
}

// Updates cart item reducer has inventory check. Returns stock check errors if any
export const checkCartStockErrors = userCartId => async dispatch => {
  return await branditRails
    .get(`/cart/${userCartId}/stock_check_errors`)
    .then(resp => {
      if (resp && resp.status === 200) {
        if (resp.data.cart_items.length > 0) {
          dispatch({ type: 'UPDATE_CART_ITEM_INVENTORY', payload: resp.data })
          toast.error('Some items in your cart are out of stock. Please update your cart to continue.')
          return resp.data
        }
      }
    })
    .catch(err => {
      toast.error(
        'Sorry! There was an issue with checking the stock of your cart items. Please try again. If issue presist, please contact our support team for assistance.'
      )
    })
}

// END CART

let abortController // For aborting tax requests

export const fetchTax = formValues => async dispatch => {
  // Abort the previous request
  if (abortController) {
    abortController.abort()
  }

  // Create a new AbortController
  abortController = new AbortController()

  return await branditRails
    .post(`/tax`, formValues, {
      signal: abortController.signal // Pass the signal to your request
    })
    .then(response => {
      if (!response) return
      if (response.data && response.data.tax_status === 'FAILED') {
        if (
          response.data.error_text ===
          'The provided address contains a postal code and state combination that is not valid.'
        ) {
          dispatch({
            type: TAX_ERROR,
            payload: {
              message:
                "ERROR CALCULATING TAX RATE: We couldn't calculate the tax rate based on the address provided, please review your address to ensure it is entered correctly."
            }
          })
          dispatch({
            type: FETCH_SHIPPING_QUOTE_ERROR,
            payload:
              "ERROR CALCULATING SHIPPING RATE: We couldn't calculate the shipping rate based on the address provided, please review your address to ensure it is entered correctly."
          })
        } else {
          dispatch({
            type: TAX_ERROR,
            payload: {
              message:
                'There has been a problem estimating sales tax on your order, please check your delivery address and try again'
            }
          })
        }
      } else {
        dispatch({ type: CLEAR_TAX_ERROR, payload: response.data })
        dispatch({ type: 'SET_TAX_QUOTE', payload: response.data })
      }
      return response
    })
    .catch(error => {
      if (error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
        console.error('Request aborted:', error.message)
      } else {
        // handle error
        dispatch({ type: TAX_ERROR, payload: { message: 'There has been a problem with fetching tax information' } })
      }
    })
}

export const fetchManagedCustomerAddresses = () => async dispatch => {
  await branditRails.get(`/customer_addresses/managed_addresses`).then(response => {
    if (response && response.data) {
      dispatch({ type: FETCH_MANAGED_CUSTOMER_ADDRESSES, payload: response.data })
    }
    dispatch({ type: CLEAR_MANAGED_CUSTOMER_ADDRESSES_ERROR, payload: '' })
  })
}

// Smart address fetching based on portal configuration
export const fetchAddressesByPortalConfig = portal => async dispatch => {
  try {
    if (portal.has_address_standard && portal.has_address_managed) {
      // HYBRID PORTAL - Fetch both endpoints
      const [standardResponse, managedResponse] = await Promise.all([
        branditRails.get('/customer_addresses'),
        branditRails.get('/customer_addresses/managed_addresses')
      ])

      if (standardResponse.data) {
        dispatch({ type: FETCH_CUSTOMER_ADDRESSES, payload: standardResponse.data })
      }
      if (managedResponse.data) {
        dispatch({ type: FETCH_MANAGED_CUSTOMER_ADDRESSES, payload: managedResponse.data })
      }
    } else if (portal.has_address_standard && !portal.has_address_managed) {
      // STANDARD ONLY - Fetch standard endpoint
      const response = await branditRails.get('/customer_addresses')
      if (response.data) {
        dispatch({ type: FETCH_CUSTOMER_ADDRESSES, payload: response.data })
      }
    } else if (!portal.has_address_standard && portal.has_address_managed) {
      // MANAGED ONLY - Fetch managed endpoint
      const response = await branditRails.get('/customer_addresses/managed_addresses')
      if (response.data) {
        dispatch({ type: FETCH_MANAGED_CUSTOMER_ADDRESSES, payload: response.data })
      }
    } else {
      console.warning('🚀 Executing: NO ADDRESSES - No endpoints to fetch')
    }
    // If neither flag is true, no addresses are fetched (NO ADDRESSES case)
  } catch (error) {
    console.error('Error fetching addresses:', error)
  }
}

// CUSTOMER ADDRESSES
export const fetchCustomerAddresses = () => async dispatch => {
  dispatch({ type: 'SET_CUSTOMER_ADDRESSES_LOADING', payload: true })
  const response = await branditRails.get(`/customer_addresses`)
  if (response.data) {
    dispatch({ type: FETCH_CUSTOMER_ADDRESSES, payload: response.data })
  }
  dispatch({ type: 'SET_CUSTOMER_ADDRESSES_LOADING', payload: false })
  if (response.data.length === 0) {
    dispatch({ type: 'NO_ADDRESSES_PRESENT', payload: true })
  } else {
    dispatch({ type: 'NO_ADDRESSES_PRESENT', payload: false })
  }
  return response.data
}

// validate address, then ask User which to keep
export const validateAddress =
  ({ addressData, onSaveAddress, callbackFunction, isInternationalShipping }) =>
  async dispatch => {
    return await branditRails
      .post(`/customer_addresses/check_address_validation`, addressData)
      .then(response => {
        // create object from original address
        const originalAddress = { ...addressData, is_valid: false }
        // get suggested address from API response
        const matchedAddress = { ...response.data[0].matched_address }
        // matched_address doesn't have all fields - fill in the values to display
        const validatedAddress = mergeAddressData(originalAddress, matchedAddress)

        const addressError = response.data[0].messages?.some(message => message.code === 'a1004')

        // check if suggested address is present
        const suggestedAddressFound =
          matchedAddress &&
          !_.isEmpty(matchedAddress) &&
          Object.entries(matchedAddress)
            .filter(arr => arr[0] !== 'address_residential_indicator')
            .some(arr => arr[1]) &&
          !addressError
        // build sweetalert with address data
        const customButtonsForSweetAlert = () => {
          return (
            <Container>
              <Row>
                <Col xs={suggestedAddressFound ? 6 : 12}>
                  {formatAddress(originalAddress, 'Original Address', isInternationalShipping)}
                  <Button
                    className="w-100 mf-outline-btn"
                    onClick={() => onSaveAddress(originalAddress, callbackFunction)}
                  >
                    <strong>Use Original Address</strong>
                  </Button>
                </Col>
                {suggestedAddressFound ? (
                  <Col xs="6">
                    {formatAddress(validatedAddress, 'Suggested Address', isInternationalShipping)}
                    <Button
                      className="w-100 mf-primary-btn"
                      onClick={() => onSaveAddress(validatedAddress, callbackFunction)}
                    >
                      <strong>Use Suggested Address </strong>
                    </Button>
                  </Col>
                ) : null}
              </Row>
            </Container>
          )
        }
        const sweetAlertMessage = suggestedAddressFound
          ? 'We found the following suggested address:'
          : 'No address suggestions found.'

        dispatch(
          dispatchSweetAlert({
            customButtons: customButtonsForSweetAlert(),
            alertMessage: sweetAlertMessage,
            showCloseButton: true
          })
        )
      })
      .catch(err => console.error(err))
  }

export const createCustomerAddress = (user_id, portal_id, values) => async dispatch => {
  return branditRails
    .post(`/customer_addresse`, values)
    .then(response => {
      dispatch({ type: CREATE_CUSTOMER_ADDRESS, payload: response.data })
      toast.info('Address has been added', {
        position: toast.POSITION.TOP_CENTER,
        type: toast.TYPE.DANGER,
        className: 'success-toast',
        autoClose: 1200
      })
      return true
    })
    .catch(err => {
      toast.error(err)
    })
}

// same as above but doesn't dispatch redux and returns address from response
export const createCustomerAddressNew =
  ({ addressData, callback, isGuest, isBilling, alsoSetBilling }) =>
  async dispatch => {
    if (isGuest) {
      if (isBilling) {
        dispatch({ type: 'ADD_GUEST_ADDRESS', payload: addressData })
        dispatch({ type: 'SET_BILLING_ADDRESS', payload: addressData })
        dispatch({ type: 'SET_USE_AS_BILLING', payload: false })
      } else {
        dispatch({ type: 'ADD_GUEST_ADDRESS', payload: addressData })
        dispatch({ type: 'SET_CUSTOMER_ADDRESS', payload: addressData })
        if (alsoSetBilling) {
          dispatch({ type: 'SET_BILLING_ADDRESS', payload: addressData })
        }
      }
      if (callback) {
        callback(addressData)
      }
      dispatch({ type: 'CLEAR_SWEETALERT' })
      return
    }
    return branditRails
      .post(`/customer_addresses`, addressData)
      .then(response => {
        dispatch({ type: CREATE_CUSTOMER_ADDRESS, payload: response.data })
        if (callback) {
          // addresses are ordered by ID descending
          callback(response.data[0])
        }
        toast.info('Address has been added', {
          position: toast.POSITION.TOP_CENTER,
          type: toast.TYPE.DANGER,
          className: 'success-toast',
          autoClose: 1200
        })
        return true
      })
      .catch(err => {
        toast.error(err)
      })
      .finally(() => {
        dispatch(clearSweetAlert())
      })
  }

export const updateCustomerAddress =
  ({ address_id, values, callback = () => {} }) =>
  async dispatch => {
    return await branditRails
      .patch(`/customer_addresses/${address_id}`, values)
      .then(response => {
        // const updatedAddress = response.data.find(add => add.id === address_id)
        dispatch({ type: UPDATE_CUSTOMER_ADDRESS, payload: response.data })
        toast.info('Address has been updated', {
          position: toast.POSITION.TOP_CENTER,
          type: toast.TYPE.DANGER,
          className: 'success-toast',
          autoClose: 1200
        })
      })
      .catch(err => {
        toast.error('Could not save address - please check values and try again')
      })
      .finally(() => {
        dispatch(clearSweetAlert())
        callback()
      })
  }

export const deleteTraditionalAddress = (id, user_id, portal_id, address_type) => async dispatch => {
  await branditRails.delete(`/customer_addresses/${id}?address_type=${address_type}`).then(function (response) {
    if (response.data.errors) {
      toast.error(response.data.errors)
    } else {
      dispatch({ type: DELETE_CUSTOMER_ADDRESS, payload: response.data })
      toast.info('Address deleted successfully!', {
        position: toast.POSITION.TOP_CENTER,
        type: toast.TYPE.SUCCESS,
        className: 'address-toast',
        autoClose: 1500
      })
    }
  })
}

// ORDERS
export const fetchUserOrders = () => async dispatch => {
  try {
    const ordersResponse = await branditRails.get(`/orders/user`)

    dispatch({ type: FETCH_CLIENT_ORDERS, payload: ordersResponse.data })
  } catch (error) {
    console.error('Error fetching orders and net terms:', error)
  }
}

export const updateOrder = (order_id, value) => async dispatch => {
  await branditRails.put(`/orders/${order_id}`, value).then(response => {
    dispatch({ type: UPDATE_ORDER, payload: response.data })
  })
}

/** Get Shipping Quote */
export const fetchShippingQuote =
  (customerAddress, cartItems, multiAddressSelection = []) =>
  async dispatch => {
    const formData = new FormData()
    dispatch({ type: 'CLEAR_SHIPPING_ERRORS' })
    dispatch({ type: 'SET_SHIPPING_LOADING', payload: true })

    const destination = {
      region: customerAddress.state,
      // smart addresses do not have country_code
      country: customerAddress.address1 ? customerAddress.country : customerAddress.country_code,
      city: customerAddress.city,
      zipcode: customerAddress.zip_code ? customerAddress.zip_code : customerAddress.zip, // for smart address
      street: customerAddress.address1 ? customerAddress.address1 : customerAddress.address_line_1,
      street2: customerAddress.address2 ? customerAddress.address2 : customerAddress.address_line_2
    }

    const cart_items = cartItems.map(item => {
      return {
        cart_item_id: item.cart_item_id,
        product_id: item.product_id,
        qty: item.quantity,
        shippable_type: item.shippable_type,
        product_detail_id: item.product_detail_id
      }
    })
    formData.append('cart_items', JSON.stringify(cart_items))
    formData.append('destination', JSON.stringify(destination))
    formData.append('multiple_address_selections', JSON.stringify(multiAddressSelection))
    formData.append('order_id', 0)
    formData.append('session_id', Math.random())

    return await branditRails
      .post('/shipping', formData)
      .then(response => {
        if (response && response.data && response.data.totalCharges >= 0) {
          dispatch(clearErrors())
          dispatch({ type: 'SET_SHIPPING_QUOTE', payload: response.data })
          return true
        } else {
          dispatch({ type: FETCH_SHIPPING_QUOTE_ERROR, payload: response.data.error })
          dispatch({ type: 'SET_CHECKOUT_ERROR', payload: { shipping: true } })
          return false
        }
      })
      .catch(error => {
        if (error.response && error.response.status === 400) {
          dispatch({ type: FETCH_SHIPPING_QUOTE_ERROR, payload: error.response.data.error })
          dispatch({ type: 'SET_CHECKOUT_ERROR', payload: { shipping: true } })
        } else {
          // For 500 errors or other server errors, try to extract meaningful message from exception
          let errorMessage = 'Unknown error occurred'

          if (error.response?.data?.exception) {
            // Extract the meaningful message from the exception string
            const exceptionMatch = error.response.data.exception.match(/: (.+?)>?$/)
            if (exceptionMatch && exceptionMatch[1]) {
              errorMessage = exceptionMatch[1]
            } else {
              errorMessage = error.response.data.exception
            }
          } else if (error.response?.data?.error) {
            // Fallback to the error field if no exception
            errorMessage = error.response.data.error
          }

          dispatch({ type: PRODUCT_ERROR, payload: { message: errorMessage } })
          dispatch({ type: 'SET_CHECKOUT_ERROR', payload: { shipping: true } })
        }
      })
      .finally(() => {
        dispatch({ type: 'SET_SHIPPING_LOADING', payload: false })
      })
  }

// Promotions
export const clearCoupons = () => async dispatch => {
  dispatch({ type: 'CLEAR_COUPONS' })
}
export const clearPromotions = () => async dispatch => {
  dispatch({ type: 'CLEAR_PROMOTIONS' })
}
export const clearDiscounts = () => async dispatch => {
  dispatch({ type: 'CLEAR_DISCOUNTS' })
}
export const fetchDiscounts = discount_params => async dispatch => {
  dispatch(clearDiscounts())
  await branditRails.post(`/promotions/process_discounts`, discount_params).then(function (response) {
    dispatch({ type: CLEAR_CODE_ERRORS, payload: '' })
    dispatch({ type: CLEAR_SUCCESS, payload: '' })
    if (response && response.data && response.data.status) {
      const promoData = JSON.parse(response.data.message)
      dispatch({ type: 'APPLY_PROMOTION', payload: promoData })
      // update taxable price with new promo data
      const cartItemsWithTaxablePrice = addTaxablePriceToCartItems(promoData.updated_cart)
      const cartItemsWithPromoData = addPromoData(cartItemsWithTaxablePrice)
      dispatch({ type: 'SET_CART_ITEMS', payload: cartItemsWithPromoData })
    }
  })
}

export const fetchCoupon =
  (coupon_params, initialSubmit = true) =>
  async dispatch => {
    await branditRails.post(`/promotions/process_coupon_code`, coupon_params).then(function (response) {
      dispatch({ type: CLEAR_CODE_ERRORS, payload: '' })
      dispatch({ type: CLEAR_SUCCESS, payload: '' })
      if (response.data.status) {
        const toastId = 'coupon-toast'
        if (initialSubmit) {
          toast.success('Coupon Applied Successfully', { toastId })
        }
        const promoData = JSON.parse(response.data.message)
        dispatch({ type: 'APPLY_PROMOTION', payload: promoData })
        const cartItemsWithTaxablePrice = addTaxablePriceToCartItems(promoData.updated_cart)
        const cartItemsWithPromoData = addPromoData(cartItemsWithTaxablePrice)
        dispatch({ type: 'SET_CART_ITEMS', payload: cartItemsWithPromoData })
      } else {
        dispatch({ type: CREATE_CODE_ERROR, payload: response.data })
      }
    })
  }

export const fetchOrderDetails = order_id => async dispatch => {
  await branditRails.get(`/orders/${order_id}/details`).then(response => {
    dispatch({ type: FETCH_ORDER_DETAILS, payload: response.data, id: order_id })
  })
}

export const generateOrderInvoice =
  (order_id, callback = null) =>
  async dispatch => {
    try {
      const response = await branditRails.get(`/orders/${order_id}/generate_invoice`)
      dispatch({ type: FETCH_ORDER_INVOICE, payload: response.data, id: order_id })

      if (callback && typeof callback === 'function') {
        callback(response.data)
      }

      return response
    } catch (err) {
      toast.error(err)
    }
  }

export const fetchDPPreview = (data, config) => async dispatch => {
  dispatch({ type: CLEAR_ERRORS, payload: '' })
  clearDpPreview()
  await branditRails
    .post(`/digital_proofing/dp_preview`, data, config)
    .then(function (response) {
      if (!response.data.items_in_stock) {
        dispatch({ type: PROOFING_PREVIEW, payload: response.data })
      }
    })
    .catch(function (error) {
      dispatch({
        type: CREATE_SESSION_ERROR,
        payload: { 'PDF upload:': 'Something has gone wrong, please contact the administrator' }
      })
      throw error
    })
}

export const clearDpPreview = () => async dispatch => {
  dispatch({ type: 'CLEAR_PROOFING_PREVIEW' })
}

export const dispatchSweetAlert = propsForSweetAlert => async dispatch => {
  dispatch({
    type: 'SHOW_SWEETALERT',
    payload: propsForSweetAlert
  })
}

export const clearSweetAlert = () => async dispatch => {
  dispatch({ type: 'CLEAR_SWEETALERT' })
}

// for legacy compatibility
export const showAlert = propsForSweetAlert => dispatch => {
  dispatch(dispatchSweetAlert(propsForSweetAlert))
}

// ** LOADING
export const setSignInLoading = bool => async dispatch => {
  dispatch({ type: 'SET_SIGN_IN_LOADING', payload: bool })
}

export const setLoading = (key, bool) => async dispatch => {
  dispatch({ type: 'SET_LOADING', payload: { key, bool } })
}

export const fetchMyDownloads =
  (page = 1) =>
  async dispatch => {
    return await branditRails.get(`/my_downloads?page=${page}`).then(resp => {
      if (resp?.status === 200) {
        const pagination = JSON.parse(resp.headers['x-pagination'])
        dispatch({
          type: 'SET_MY_DOWNLOADS',
          payload: resp.data,
          total: pagination.total
        })
      }
    })
  }

export const sendCartToGreenwing = cart => async dispatch => {
  try {
    const response = await branditRails.post('cart/send_to_greenwing', cart)

    return response.data
  } catch (err) {
    toast.error(err.message ?? 'There was a problem proccessing your purchase request.')
    console.error(err)
  }
}

// Payment Gateway Actions
export const processPaymentNonce = paymentData => async (dispatch, getState) => {
  try {
    const token = getState()?.checkout?.checkoutSessionToken
    const response = await branditRails.post('/payment_gateway/process_credit_card', {
      ...paymentData,
      checkout_session_token: paymentData.checkout_session_token || token
    })

    if (response.data.success) {
      dispatch({ type: 'PAYMENT_SUCCESS', payload: response.data })
      toast.success('Payment processed successfully!')
    } else {
      dispatch({ type: 'PAYMENT_ERROR', payload: response.data.error })
      toast.error(response.data.error || 'Payment processing failed')
    }

    return response.data
  } catch (err) {
    console.error('Payment processing error: 9', err)
    const errorMessage = err.response?.data?.error || err.message || 'Payment processing failed'
    dispatch({ type: 'PAYMENT_ERROR', payload: errorMessage })
    toast.error(errorMessage)
    throw err
  }
}
